import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, User, Building2, Mail, Phone, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import SearchableSelect from '@/components/common/SearchableSelect';
import * as contactsService from '@/services/api/contactsService';
import * as accountsService from '@/services/api/accountsService';
import type { Contact } from '@/services/api/contactsService';
import type { Account } from '@/services/api/accountsService';

export default function AddEditContactPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountLabel, setAccountLabel] = useState('');
  const [formData, setFormData] = useState({
    accountId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    role: '',
    department: '',
    currentStage: 'Stage1',
    status: 'COLD'
  });

  // Fetch accounts for dropdown
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch accounts');
        }
        
        const data = await response.json();
        setAccounts(data.data || []);
      } catch (error: any) {
        console.error('Error fetching accounts:', error);
        addToast({
          type: 'error',
          title: 'Error',
          description: 'Failed to load accounts list'
        });
      }
    };

    fetchAccounts();
  }, []);

  // Fetch contact data if editing
  useEffect(() => {
    if (!isEditMode) {
      setLoading(false);
      return;
    }

    const fetchContact = async () => {
      try {
        const contactData = await contactsService.getContact(id!);
        setFormData({
          accountId: contactData.accountId || '',
          firstName: contactData.firstName || '',
          lastName: contactData.lastName || '',
          email: contactData.email || '',
          phone: contactData.phone || '',
          title: contactData.title || '',
          role: contactData.role || '',
          department: contactData.department || '',
          currentStage: contactData.currentStage || 'Stage1',
          status: contactData.status || 'COLD'
        });
        setAccountLabel((contactData as any).account?.name || '');
      } catch (error: any) {
        console.error('Error fetching contact:', error);
        addToast({
          type: 'error',
          title: 'Error',
          description: 'Failed to load contact data'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [id, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        description: 'First name, last name, and email are required'
      });
      return;
    }

    if (!formData.accountId) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        description: 'Please select an account'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const apiBase = import.meta.env.VITE_API_URL ?? '';
      const url = isEditMode 
        ? `${apiBase}/api/contacts/${id}`
        : `${apiBase}/api/contacts`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const payload = {
        ...formData
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save contact');
      }

      addToast({
        type: 'success',
        title: 'Success',
        description: isEditMode ? 'Contact updated successfully' : 'Contact created successfully'
      });

      navigate('/contacts');
    } catch (error: any) {
      console.error('Error saving contact:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: error.message || 'Failed to save contact'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        onBack={() => navigate('/contacts')}
        title={isEditMode ? 'Edit Contact' : 'Add New Contact'}
        description={isEditMode ? 'Update contact information' : 'Create a new contact in the system'}
      />

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    First Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="John"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Last Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Smith"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="john.smith@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="+1-234-567-8900"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Association */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Account Association
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Account <span className="text-destructive">*</span>
                </label>
                <SearchableSelect
                  value={formData.accountId}
                  valueLabel={accountLabel}
                  placeholder="Search account…"
                  onChange={(id, opt) => { setFormData({ ...formData, accountId: id }); setAccountLabel(opt?.label || ''); }}
                  fetchOptions={async (q) => {
                    const res = await accountsService.getAccounts({ limit: 25, ...(q ? { search: q } : {}) } as any);
                    return (res.data || []).map((a: any) => ({ id: a.id, label: a.name, sublabel: [a.industryVertical, a.region].filter(Boolean).join(' · ') }));
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Professional Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Professional Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="VP of Engineering"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Technical Decision Maker"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Engineering"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Current Stage</label>
                  <select
                    value={formData.currentStage}
                    onChange={(e) => setFormData({ ...formData, currentStage: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Stage1">Stage 1 - Initial Contact</option>
                    <option value="Stage2">Stage 2 - Qualification</option>
                    <option value="Stage3">Stage 3 - Discovery</option>
                    <option value="Stage4">Stage 4 - Proposal</option>
                    <option value="Stage5">Stage 5 - Negotiation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="COLD">Cold</option>
                    <option value="ENGAGED">Engaged</option>
                    <option value="ACTIVE">Active</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/contacts')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update Contact' : 'Create Contact'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
